import { Component, ChangeDetectorRef } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { SharedService } from 'src/app/shared/services/shared.service';

@Component({
  selector: 'app-re-cert-btn',
  standalone: true,
  imports: [],
  templateUrl: './re-cert-btn.component.html',
  styleUrl: './re-cert-btn.component.scss',
})
export class ReCertBtnComponent implements ICellRendererAngularComp {
  params: any;
  recertBtnVisible = false;

  constructor(
    public sharedService: SharedService,
    private cd: ChangeDetectorRef
  ) {}

  agInit(params: any): void {
    this.params = params;
    const row = params?.node?.data ?? params?.data ?? null;

    // Build reserved date from transferee_cert_day/month/year if available,
    // otherwise fall back to reserved_date string parsing.
    let reserved: Date | null = null;
    const day = row?.transferee_cert_day ?? null;
    const month = row?.transferee_cert_month ?? null;
    const year = row?.transferee_cert_year ?? null;
    if (day && month && year) {
      const d = Number(day);
      const m = Number(month);
      const y = Number(year);
      if (!Number.isNaN(d) && !Number.isNaN(m) && !Number.isNaN(y)) {
        reserved = this.normalizeToMidnight(new Date(y, m - 1, d));
      }
    }
    if (!reserved) {
      const reservedDateRaw = row?.reserved_date ?? row?.reservedDate ?? null;
      reserved = this.parseReservedDate(reservedDateRaw);
    }

    const today = this.normalizeToMidnight(new Date());

    // Show button only when a valid reserved date exists and it is NOT today
    this.recertBtnVisible = !!reserved && !this.isSameDay(reserved, today);

    // ensure Angular updates the template
    this.cd.detectChanges();
  }

  recertClick(): void {
    const rowData = {
      colValues: this.params.node.data,
      rowIndex: this.params.rowIndex,
    };
    this.params.recertClick(rowData);
  }

  refresh(params?: any): boolean {
    if (params) {
      this.params = params;
      const row = params?.node?.data ?? params?.data ?? null;

      let reserved: Date | null = null;
      const day = row?.transferee_cert_day ?? null;
      const month = row?.transferee_cert_month ?? null;
      const year = row?.transferee_cert_year ?? null;
      if (day && month && year) {
        const d = Number(day);
        const m = Number(month);
        const y = Number(year);
        if (!Number.isNaN(d) && !Number.isNaN(m) && !Number.isNaN(y)) {
          reserved = this.normalizeToMidnight(new Date(y, m - 1, d));
        }
      }
      if (!reserved) {
        const reservedDateRaw = row?.reserved_date ?? row?.reservedDate ?? null;
        reserved = this.parseReservedDate(reservedDateRaw);
      }

      this.recertBtnVisible = !!reserved && !this.isSameDay(reserved, this.normalizeToMidnight(new Date()));
      this.cd.detectChanges();
    }
    return true;
  }

  private parseReservedDate(input: string | Date | null): Date | null {
    if (!input) return null;
    if (input instanceof Date) return this.normalizeToMidnight(input);

    const s = String(input).trim();

    // Accept MM-DD-YYYY, MM/DD/YYYY, and YYYY-MM-DD
    let m;
    m = s.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/); // MM-DD-YYYY
    if (m)
      return this.normalizeToMidnight(
        new Date(Number(m[3]), Number(m[1]) - 1, Number(m[2]))
      );

    m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/); // MM/DD/YYYY
    if (m)
      return this.normalizeToMidnight(
        new Date(Number(m[3]), Number(m[1]) - 1, Number(m[2]))
      );

    m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/); // YYYY-MM-DD
    if (m)
      return this.normalizeToMidnight(
        new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
      );

    const parsed = new Date(s);
    return isNaN(parsed.getTime()) ? null : this.normalizeToMidnight(parsed);
  }

  private normalizeToMidnight(d: Date): Date {
    const n = new Date(d);
    n.setHours(0, 0, 0, 0);
    return n;
  }

  private isSameDay(a: Date | null, b: Date | null): boolean {
    if (!a || !b) return false;
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  }
}